import { TCart } from 'widgets/cart/CartMgr';
import { TProduct as TBaseProduct, TAvailabilityModel } from 'types/models/product';
import { TProcessButtonInstance } from 'widgets/global/ProcessButton';
import { TAddToCartMixinInstance } from 'widgets/product/AddToCartMixin';
import { IAddToCartButton } from 'widgets/product/AddToCartMixin';
import { TCoreProductDetailInstance } from 'core/product/CoreProductDetail';
import { scrollWindowTo } from 'widgets/toolbox/scroll';

export type TProduct = TBaseProduct & {
    price: TBaseProduct['price'] & { html: string };
    stock: null | number;
    availability: TAvailabilityModel;
};

type TAddAllToCartResponse = TServerResponse & {
    cart: TCart & {
        addedProducts?: Array<{ gtmInfo?: { item_id?: string } }>;
        addedProduct?: { gtmInfo?: { item_id?: string } };
    };
    messages: Array<unknown>;
};

// Base TProductSetItem type that's compatible with the parent class
type TBaseProductSetItem = {
    pid: string;
    quantity: number;
    isAvailable: boolean;
    isAllAttributesSelected: boolean;
    hasAvailableVariant: boolean;
    widget: TAddToCartMixinInstance;
};

// Extended TProductSetItem type for core implementation
type TProductSetItem = TBaseProductSetItem & {
    isOutOfStock: boolean;
    widget: TAddToCartMixinInstance | TCoreProductDetailInstance;
};

/**
 * @description Core AddAllToCartMixin implementation
 *
 * This class is not intended to have a separate DOM representation.
 *
 * @param AddAllToCartMixin Base widget for extending
 * @returns Add to Cart mixin widget
 */
function CoreAddAllToCartMixinClassCreator(AddAllToCartMixin: import('widgets/product/AddAllToCartMixin').TAddAllToCartMixin) {
    /**
     * @category widgets
     * @subcategory product
     * @class CoreAddAllToCartMixin
     * @augments AddAllToCartMixin
     * @classdesc Represents product set component with the next features:
     * 1. Adds all product set products to the basket
     * 2. Shows alert message after adding product set products to the basket
     *
     * @property {string} data-text-network-error - network error text
     */
    class CoreAddAllToCartMixin extends AddAllToCartMixin {
        prefs() {
            return {
                priceTemplate: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.eventBus().on('product.price.updated', 'updateSetPrice');
        }

        /**
         * @description Update set price based on selected variants
         */
        updateSetPrice(): void {
            const productSetItems = this.getProductSetItems();
            const invalidProducts = productSetItems.filter(setItem => !setItem.isAllAttributesSelected && setItem.hasAvailableVariant);

            const totalPrice = this.calculateSetPrice(productSetItems);
            const formattedPrice = this.formatPrice(totalPrice);

            if (invalidProducts.length) {
                this.renderSetPrice({
                    minSetPrice: formattedPrice
                });
            } else {
                this.renderSetPrice({
                    salesSetPrice: formattedPrice
                });
            }
        }

        /**
         * @description Calculated formatted price based on selected variants
         * @param productSetItems Array of product set items
         */
        calculateSetPrice(productSetItems): number {
            let totalPrice = 0;

            productSetItems.forEach(setItem => {
                setItem.widget.getById('priceBlock', priceBlock => {
                    totalPrice += priceBlock.prefs().price || 0;
                });
            });

            return totalPrice;
        }

        /**
         * @description Formats the total price using the configured price template
         * @param totalPrice The numeric price value to format
         * @returns The formatted price string with proper locale formatting
         */
        formatPrice(totalPrice: number): string {
            const priceTemplate = this.prefs().priceTemplate;
            const formattedPrice = totalPrice.toFixed(2);

            if (priceTemplate.includes('0,00')) {
                return priceTemplate.replace('0,00', formattedPrice.replace('.', ','));
            }

            return priceTemplate.replace('0.00', formattedPrice);
        }

        /**
         * @description Render set product price
         * @param price Price set object
         */
        renderSetPrice(price): void {
            this.getById('setPriceBlock', setPriceBlock => {
                setPriceBlock.render('setPriceInfo', price, setPriceBlock.ref('setPriceInfoInner'));
            });
        }

        /**
         * @description Handles a result of product set adding to cart
         * @emits "minicart.need.update"
         * @emits "product.set.added.to.cart"
         * @param response - response
         * @param isFullProductSet - Identifies if all product set items are added
         * @param productsCount - products count to be added to the cart
         */
        postAddAllToCart(response: TAddAllToCartResponse, isFullProductSet: boolean, productsCount: number) {
            if (response.success && response.cart && response.messages) {
                const cartModel = response.cart;

                cartModel.showMinicart = this.prefs().showMinicartOnProductAdd;

                if (response.addToCartOverlayUrl) {
                    const analyticsOnCancel = this.getAnalyticsOnCancel(response);

                    this.eventBus().emit('drawer.cancel');
                    this.eventBus().emit('drawershow', {
                        url: response.addToCartOverlayUrl,
                        headerText: response.addToCartOverlayHeader,
                        ariaLabel: response.addToCartOverlayHeader,
                        bodyClass: 'm-has_dialog',
                        wrapperClasses: 'm-cart_overlay',
                        analyticsOnCancel: analyticsOnCancel
                    });
                }

                const isAllProductsAdded = response.messages && response.messages.length === productsCount;
                const message =
                    isFullProductSet && isAllProductsAdded ? this.prefs().accessibilityAlerts.addedsettocart : response.messages.join('<br>');

                if (this.prefs().showMessageOnProductAdd) {
                    this.showAddAllToCartAlert(false, message);
                }

                this.eventBus().emit('minicart.need.update', cartModel, this);
                this.eventBus().emit('product.set.added.to.cart', cartModel, this);
            } else {
                this.showAddAllToCartAlert(true, response.errorMessage);
            }
        }

        /**
         * @description Gets analytics data for the close overlay
         * @param response Server response from add to cart action
         * @returns Analytics data object or null if no item ID is found
         */
        getAnalyticsOnCancel(response: TAddAllToCartResponse) {
            let itemId;

            if (response.cart?.addedProducts?.length) {
                response.cart?.addedProducts
                    ?.reduce((arr: Array<string>, item: { gtmInfo?: { item_id?: string } }) => {
                        const id = item?.gtmInfo?.item_id;

                        if (id) {
                            arr.push(id);
                        }

                        return arr;
                    }, [])
                    .join(';');
            } else {
                itemId = response.cart?.addedProduct?.gtmInfo?.item_id;
            }

            if (!itemId) {
                return null;
            }

            return {
                event_name: 'close_overlay',
                close_overlay: {
                    feature: 'atc_overlay',
                    item_id: itemId
                }
            };
        }

        /**
         * @description Returns product set data for the set items
         * @returns result
         */
        getProductSetItems(): Array<TProductSetItem> {
            const productSetItems: Array<TProductSetItem> = [];

            this.eachChild(child => {
                const assertedChild = child as TAddToCartMixinInstance;

                if (assertedChild.prefs().productSetItem) {
                    let isOutOfStock = false;

                    assertedChild.getById<IAddToCartButton>('addToCart', addToCart => {
                        isOutOfStock = Boolean(addToCart.prefs().isOutOfStock);
                    });

                    const prefs = assertedChild.prefs();

                    productSetItems.push({
                        pid: prefs.currentProductId || prefs.pid,
                        quantity: prefs.selectedQuantity,
                        isOutOfStock: isOutOfStock,
                        isAvailable: prefs.readyToOrder && !isOutOfStock, // is selected and is available
                        isAllAttributesSelected: prefs.isAllAttributesSelected, // is selected but could be unavailable
                        hasAvailableVariant: prefs.hasAvailableVariant, // at least one available variant could be selected
                        widget: assertedChild
                    });
                }
            });

            return productSetItems;
        }

        /**
         * @description Disables process button if all products are unavailable, enables otherwise
         * @param productSetItems list for products for adding to the cart
         */
        updateAddAllToCardButton(productSetItems: Array<TProductSetItem>) {
            this.getById<TProcessButtonInstance>('addToCardGlobal', processButton => {
                if (productSetItems.some(product => product.isOutOfStock)) {
                    processButton.disable();
                    processButton.setText(processButton.config.outOfStockLabel as string);
                } else {
                    processButton.enable();
                    processButton.setText(processButton.config.addToCartSetLabel as string);
                }
            });
        }

        /**
         * @description Handler for unselected but available product variations
         * @param productSetItems list for products for adding to the cart
         * @returns true if error was set, false otherwise
         */
        handleUnselected(productSetItems: Array<TProductSetItem>): boolean {
            return this.handleProductDetailsError(productSetItems, setItem => !setItem.isAllAttributesSelected && setItem.hasAvailableVariant);
        }

        /**
         * @description Shows error message for all invalid products and sets focus on the first product with error
         * @param productSetItems - product list for validation and setting an error
         * @param invalidProductFilter - predicate, which points on invalid product
         * @returns true if error was set, false otherwise
         */
        handleProductDetailsError(productSetItems: Array<TProductSetItem>, invalidProductFilter: (item: TProductSetItem) => boolean) {
            const invalidProducts = productSetItems.filter(invalidProductFilter);

            if (!invalidProducts.length) {
                return false;
            }

            invalidProducts.forEach(productSetItem => productSetItem.widget.callIfExists('checkUnselectedAttributes'));

            const firstInvalidProductWidget = invalidProducts[0].widget;

            firstInvalidProductWidget.getById('productName', productName => {
                const firstUnselectedProductTitle = productName.ref('titleLink').get();

                if (!firstUnselectedProductTitle) {
                    return;
                }

                firstUnselectedProductTitle.focus();

                scrollWindowTo(firstUnselectedProductTitle, true);
            });

            return true;
        }

        /**
         * @description stub method to allow adding product set to wishlist
         * and ignore selected variations attributes
         * @returns true
         */
        checkUnselectedAttributes(): boolean {
            return true;
        }
    }

    return CoreAddAllToCartMixin;
}

export type TCoreAddAllToCartMixin = ReturnType<typeof CoreAddAllToCartMixinClassCreator>;

export type TCoreAddAllToCartMixinInstance = InstanceType<TCoreAddAllToCartMixin>;

export default CoreAddAllToCartMixinClassCreator;
